import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell, Loader2, RefreshCw, Send } from "lucide-react";
import { AdminNav, AdminOnly } from "@/components/admin/AdminNav";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { adminListNotifications, adminResendNotification } from "@/lib/notifications.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/notifications")({
  head: () => ({ meta: [{ title: "Notifications log — Lumvio admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <NotificationsAdminPage />
    </AdminOnly>
  ),
});

function NotificationsAdminPage() {
  const list = useServerFn(adminListNotifications);
  const resend = useServerFn(adminResendNotification);
  const qc = useQueryClient();

  const [channel, setChannel] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [event, setEvent] = useState("");
  const [userId, setUserId] = useState("");

  const filters = useMemo(() => ({
    channel: channel === "all" ? undefined : (channel as any),
    status: status === "all" ? undefined : (status as any),
    event: event.trim() || undefined,
    userId: userId.trim() || undefined,
    limit: 200,
  }), [channel, status, event, userId]);

  const q = useQuery({
    queryKey: ["admin-notifications", filters],
    queryFn: () => list({ data: filters as any }),
  });

  const resendMut = useMutation({
    mutationFn: async (id: string) => resend({ data: { id } }),
    onSuccess: () => { toast.success("Resent"); qc.invalidateQueries({ queryKey: ["admin-notifications"] }); },
    onError: (e: any) => toast.error(e?.message || "Resend failed"),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        icon={Bell} title="Notifications log"
        subtitle="Every message sent by Lumvio. Filter, inspect, and resend failed messages."
      />

      <div className="flex flex-wrap items-center gap-2 rounded-2xl border bg-card/50 p-3">
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Channel" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All channels</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="sms">SMS</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Event type" value={event} onChange={(e) => setEvent(e.target.value)} className="w-[180px]" />
        <Input placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} className="w-[260px]" />
        <Button size="sm" variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["admin-notifications"] })}>
          <RefreshCw className="me-1 h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border">
        <div className="w-full overflow-x-auto [-webkit-overflow-scrolling:touch]">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-start">When</th>
              <th className="px-3 py-2 text-start">Event</th>
              <th className="px-3 py-2 text-start">Channel</th>
              <th className="px-3 py-2 text-start">Status</th>
              <th className="px-3 py-2 text-start">To</th>
              <th className="px-3 py-2 text-start">Provider ID</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
            )}
            {(q.data ?? []).map((r: any) => (
              <tr key={r.id} className="border-t hover:bg-muted/20">
                <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 font-medium">{r.event_type}</td>
                <td className="px-3 py-2">{r.channel}</td>
                <td className="px-3 py-2">
                  <span className={
                    r.status === "sent" || r.status === "delivered" ? "text-emerald-500" :
                    r.status === "failed" ? "text-rose-500" : "text-muted-foreground"
                  }>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-xs">{r.to_address}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[220px]">{r.provider_message_id || r.error || "—"}</td>
                <td className="px-3 py-2 text-end">
                  {r.status === "failed" && (
                    <Button size="sm" variant="outline" disabled={resendMut.isPending} onClick={() => resendMut.mutate(r.id)}>
                      <Send className="me-1 h-3 w-3" /> Resend
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {q.data && q.data.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No messages yet.</td></tr>
            )}
          </tbody>
        </table>
          </div>
      </div>
    </div>
  );
}
